import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import {
  faFaceGrinWide, faFaceSunglasses, faLockAlt,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type FormEvent, useMemo, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { LinkComponent } from '~/_shared/baseComponents/links';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { PasswordStrengthIndicatorComponent } from '~/_shared/components/passwordStrengthIndicator/passwordStrengthIndicator.component';
import { TermsAndPrivacyNoticeComponent } from '~/_shared/components/termsAndPrivacyNotice/termsAndPrivacyNotice.component';
import { CONFIG } from '~/_shared/constants/config';
import { LOGIN_ROUTE } from '~/_shared/constants/routes';
import {
  Breakpoint, mq,
} from '~/_shared/style.mixins';
import type { Theme } from '~/_shared/themes/theme.model';
import { FormFieldType } from '~/_shared/utils/form/formValidation.helpers';
import { useFormFieldValidation } from '~/_shared/utils/form/useFormFieldValidation';
import { useTranslation } from '~/_shared/utils/hooks';
import { SsoButtonsComponent } from '~/authorization/loginPage/sso/ssoButtons.component';
import { type UserRegisterErrors } from '~/store/userData/repository/userData.repository';
import { authorizationPagesErrorStyle } from '../../authorizationPages.styles';
import { AuthorizationSubmitButtonComponent } from '../../submitButton/authorizationSubmitButton.component';
import { validateRegisterForm } from './registerForm.helpers';
import { type UserRegisterData } from './useUserRegister';

const ITEMS_GAP = 16;

const alertStyle = css({
  marginTop: 10,
});

const defaultCursorStyle = css({
  cursor: 'default',
});

const centerTextStyle = css(defaultCursorStyle, {
  textAlign: 'center',
});

const headingPrimaryStyle = css(centerTextStyle, {
  fontSize: 24,
  margin: 0,
});

const formBodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: ITEMS_GAP,
});

const privacyTextStyle = css({
  fontSize: 18,
  marginLeft: 8,
});

const headingSecondaryStyle = css(centerTextStyle, {
  fontSize: 16,
  margin: '8px 0 30px',
});

const passwordWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const rowWithColumnsStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: ITEMS_GAP,
  [mq(Breakpoint.mid)]: {
    flexDirection: 'row',
    gap: 8,
  },
});

const columnStyle = css({
  flex: 1,
  minWidth: 0,
});

const passwordStrengthWrapperStyle = css({
  fontSize: 14,
  width: '80%',
  margin: '0 auto',
});

const buttonWrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'space-between',

  [mq(Breakpoint.mid)]: {
    flexDirection: 'row',
  },
});

const alreadyUserSectionStyle = css(defaultCursorStyle, {
  fontSize: 18,
  fontWeight: 700,
  textAlign: 'start',
  width: '100%',

  [mq(Breakpoint.mid)]: {
    width: 'unset',
  },
});

const sigUpButtonStyle = css({
  width: '85%',

  [mq(Breakpoint.mid)]: {
    width: 'unset',
  },
});

const alreadyUserLinkStyle = css({
  fontSize: 18,
});

const enterpriseSectionStyle = css(defaultCursorStyle, {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: 14,
  marginTop: 40,
});

const enterpriseHeadingStyle = css({
  fontWeight: 700,
  margin: 0,
});

const enterpriseDescriptionStyle = css({
  margin: 0,
});

const orDividerWrapperStyle = css({
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
});

const orDividerLineStyle = (theme: Theme) => css({
  flex: 1,
  height: 1,
  backgroundColor: theme.lineColors.basePrimary,
});

const orDividerTextStyle = (theme: Theme) => css({
  padding: '0 12px',
  fontSize: 12,
  color: theme.textColors.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
});

type RegisterFormProps = Readonly<{
  errorMessage?: string;
  errors?: UserRegisterErrors | null;
  isLoading: boolean;

  closeErrorMessage: () => void;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  onSubmit: (data: UserRegisterData) => void;
}>;

export const RegisterFormComponent: FC<RegisterFormProps> = (props) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [t] = useTranslation();

  const { value: firstName, onBlur: onFirstNameBlur, onChange: onFirstNameChange, errors: firstNameErrors } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('First name must not be empty'),
  });
  const { value: lastName, onBlur: onLastNameBlur, onChange: onLastNameChange, errors: lastNameErrors } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('Last name must not be empty'),
  });
  const { value: email, onBlur: onEmailBlur, onChange: onEmailChange, errors: emailErrors } = useFormFieldValidation({
    type: FormFieldType.Email,
    additionalErrors: props.errors?.email,
  });

  const { value: password, onBlur: onPasswordBlur, onChange: onPasswordChange, errors: passwordErrors } = useFormFieldValidation({
    type: FormFieldType.Password,
    additionalErrors: props.errors?.password,
  });

  const isFormValid = useMemo(() =>
    validateRegisterForm({ firstName, lastName, email, password }),
  [email, firstName, lastName, password]);

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }

    const userRegisterData: UserRegisterData = {
      firstName,
      lastName,
      email,
      password,
    };

    if (!validateRegisterForm(userRegisterData)) {
      return;
    }

    props.onSubmit(userRegisterData);
  };

  return (
    <form
      onSubmit={onFormSubmit}
      autoComplete="off"
    >
      <p css={headingPrimaryStyle}>{t('Start your free, no risk, 10 day trial.')}</p>
      <p css={headingSecondaryStyle}>{t('No credit card required. No surprises. Just results.')}</p>

      <div css={formBodyStyle}>

        <div>
          <CheckboxComponent
            textStyle={privacyTextStyle}
            checkedSetter={(event) => {
              const checked = event.target.checked;
              setTermsAccepted(checked);
              setShowTermsError(!checked);
            }}
            isChecked={termsAccepted}
            text={<TermsAndPrivacyNoticeComponent />}
          />

          {showTermsError && (
            <FormErrorMessageComponent
              css={authorizationPagesErrorStyle}
              isSingleLine={false}
              messages={[t('signup.agreement.warning')]}
            />
          )}
        </div>

        {CONFIG.ENABLE_SSO_LOGIN && (
          <div>
            <SsoButtonsComponent
              isDisabled={props.isLoading || !termsAccepted}
              onGoogleLogin={props.onGoogleLogin}
              onMicrosoftLogin={props.onMicrosoftLogin}
            />
            <div css={orDividerWrapperStyle}>
              <div css={orDividerLineStyle} />
              <div css={orDividerTextStyle}>
                {t('or')}
              </div>
              <div css={orDividerLineStyle} />
            </div>
          </div>
        )}

        <div css={rowWithColumnsStyle}>
          <div css={columnStyle}>
            <TextInputAuthComponent
              label={t('First Name')}
              placeholder={t('First Name')}
              autoComplete="given-name"
              value={firstName}
              onChange={onFirstNameChange}
              onBlur={onFirstNameBlur}
              hasError={firstNameErrors.length > 0}
              icon={faFaceGrinWide}
              isDisabled={props.isLoading}
            />

            {firstNameErrors.length > 0 && (
              <FormErrorMessageComponent
                css={authorizationPagesErrorStyle}
                isSingleLine={false}
                messages={firstNameErrors}
              />
            )}
          </div>

          <div css={columnStyle}>
            <TextInputAuthComponent
              label={t('Last Name')}
              placeholder={t('Last Name')}
              autoComplete="family-name"
              value={lastName}
              onChange={onLastNameChange}
              onBlur={onLastNameBlur}
              hasError={lastNameErrors.length > 0}
              icon={faFaceSunglasses}
              isDisabled={props.isLoading}
            />

            {lastNameErrors.length > 0 && (
              <FormErrorMessageComponent
                css={authorizationPagesErrorStyle}
                isSingleLine={false}
                messages={lastNameErrors}
              />
            )}
          </div>
        </div>

        <div css={columnStyle}>
          <TextInputAuthComponent
            label={t('Email')}
            placeholder={t('Email')}
            value={email}
            onChange={onEmailChange}
            onBlur={onEmailBlur}
            hasError={emailErrors.length > 0}
            icon={faEnvelope}
            isDisabled={props.isLoading}
          />

          {emailErrors.length > 0 && (
            <FormErrorMessageComponent
              css={authorizationPagesErrorStyle}
              isSingleLine={false}
              messages={emailErrors}
            />
          )}
        </div>

        <div css={passwordWrapperStyle}>
          <div>
            <TextInputAuthComponent
              label={t('Password')}
              placeholder={t('Password')}
              value={password}
              type="password"
              autoComplete="new-password"
              onChange={onPasswordChange}
              onBlur={onPasswordBlur}
              hasError={passwordErrors.length > 0}
              icon={faLockAlt}
              isDisabled={props.isLoading}
            />

            {passwordErrors.length > 0 && (
              <FormErrorMessageComponent
                css={authorizationPagesErrorStyle}
                isSingleLine={false}
                messages={passwordErrors}
              />
            )}
          </div>

          <div css={passwordStrengthWrapperStyle}>
            <PasswordStrengthIndicatorComponent password={password} />
          </div>
        </div>

        <div css={buttonWrapperStyle}>

          <p css={alreadyUserSectionStyle}>
            {t('Already a user?') + ' '}
            <LinkComponent
              css={alreadyUserLinkStyle}
              path={LOGIN_ROUTE}
            >
              {t('Log in here')}
            </LinkComponent>
          </p>

          <div css={sigUpButtonStyle}>
            <AuthorizationSubmitButtonComponent
              isLoading={props.isLoading}
              isDisabled={!isFormValid}
            >
              {t('Create Account')}
            </AuthorizationSubmitButtonComponent>
          </div>

        </div>
      </div>

      {props.errorMessage && !props.errors && (
        <AlertComponent
          css={alertStyle}
          onClose={props.closeErrorMessage}
          stylingOptions={{ withoutBorder: true }}
          type="dangerAlternative"
        >
          {props.errorMessage}
        </AlertComponent>
      )}

      <div css={enterpriseSectionStyle}>
        <p css={enterpriseHeadingStyle}>{t('signup.plans.enterpriseAvailable')}</p>
        <p css={enterpriseDescriptionStyle}>{t('signup.plans.customTerms')}</p>
      </div>
    </form>
  );
};
